<script lang="ts">
	import type { URLState } from '../url-state';
	import { navigateTo } from '../url-state';
	import { handleApiError, handleNetworkError } from '../utils/errors';
	import { toast } from '../stores/toast';

	let { urlState } = $props<{
		urlState: URLState;
	}>();

	let files = $state<FileList | null>(null);
	let uploading = $state(false);

	async function handleImport() {
		if (!files || files.length === 0) {
			toast.warning('Selecteer eerst een bestand om te importeren');
			return;
		}

		// Validate file type
		const file = files[0];
		if (!file.name.toLowerCase().endsWith('.csv')) {
			toast.warning('Selecteer een CSV bestand');
			return;
		}

		// Validate file size (max 10MB)
		const maxSize = 10 * 1024 * 1024; // 10MB
		if (file.size > maxSize) {
			toast.error('Bestand is te groot (maximaal 10MB toegestaan)');
			return;
		}

		uploading = true;

		try {
			const formData = new FormData();
			formData.append('csvfile', file);

			const response = await fetch('/api/import', {
				method: 'POST',
				body: formData
			});

			if (response.ok) {
				const result = await response.json();
				if (result.success) {
					let successMessage = `${result.message}. Geïmporteerd: ${result.imported || 0}`;
					
					if (result.skipped && result.skipped > 0) {
						successMessage += `, Overgeslagen: ${result.skipped}`;
					}

					if (result.errors && result.errors.length > 0) {
						toast.success(successMessage, {
							details: result.errors.slice(0, 5) // Show max 5 errors in toast
						});
						
						if (result.errors.length > 5) {
							console.warn('Additional import warnings:', result.errors.slice(5));
						}
					} else {
						toast.success(successMessage);
					}
					
					// Reset file input
					files = null;
					// Navigate home without dates so the date range is re-initialized
					navigateTo('/');
				} else {
					const errorResult = {
						message: `Import mislukt: ${result.message}`,
						type: 'error' as const,
						details: result.errors || undefined
					};
					toast.apiError(errorResult);
				}
			} else {
				const errorResult = await handleApiError(response);
				toast.apiError(errorResult);
			}
		} catch (error) {
			console.error('Import error:', error);
			if (error instanceof Error) {
				const errorResult = handleNetworkError(error);
				toast.apiError(errorResult);
			} else {
				toast.error('Import mislukt: Onbekende fout');
			}
		} finally {
			uploading = false;
		}
	}
</script>

<main>
	<h1><a href="/" onclick={(e) => { e.preventDefault(); navigateTo('/', { from: urlState.from, until: urlState.until }); }}>Overzicht</a> > Importeer transacties</h1>
	<div class="import-form">
		<p>Selecteer een CSV bestand om te importeren:</p>

		<input type="file" accept=".csv" bind:files disabled={uploading} />

		<button onclick={handleImport} disabled={uploading || !files || files.length === 0}>
			{uploading ? 'Uploaden...' : 'Importeer'}
		</button>

		<div class="info">
			<h3>Ondersteunde formaten:</h3>
			<ul>
				<li>ING CSV export</li>
				<li>Triodos CSV export</li>
				<li>Van Lanschot CSV export</li>
			</ul>
		</div>
	</div>
</main>

<style>
	.import-form {
		max-width: 500px;
		margin-top: 2em;
	}

	.import-form > * {
		margin-bottom: 1em;
	}

	button {
		padding: 0.5em 1em;
		border: 1px solid #ccc;
		border-radius: 4px;
		background: white;
		cursor: pointer;
	}

	button:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	button:hover:not(:disabled) {
		background: #f0f0f0;
	}

	.info {
		margin-top: 2em;
		padding: 1em;
		background: #f8f9fa;
		border-radius: 4px;
	}

	.info h3 {
		margin-top: 0;
	}
</style>
